<script>
import Quill from 'quill';
window.Quill = Quill;
import 'quill/dist/quill.snow.css';
import { quillEditor } from 'vue-quill-editor';
import SelectDropdown from './SelectDropdown.vue';
import ColorPicker from './ColorPicker.vue';
import { fontList, fontSizeList } from '../utils/staticData';

const ImageResize = require('quill-image-resize-module').default;
Quill.register('modules/imageResize', ImageResize);

// Register custom fonts with Quill
const Font = Quill.import('formats/font');
Font.whitelist = [
    'Arial', 'Calibri', 'Garamond', 'Georgia', 'Helvetica', 'Inter', 'Italic',
    'Lato', 'Manrope', 'Montserrat', 'Open Sans', 'Oswald', 'Playfair Display',
    'Poppins', 'Roboto', 'sans-serif', 'Satoshi', 'Times New Roman', 'Urbanist', 'Verdana'
];
Quill.register(Font, true);

// Register custom font sizes with Quill (1 to 200px)
const Size = Quill.import('formats/size');
Size.whitelist = Array.from({ length: 200 }, (_, i) => String(i + 1));
Quill.register(Size, true);

export default {
    name: "TextEditor",
    components: {
        quillEditor,
        SelectDropdown,
        ColorPicker,
    },
    props: {
        value: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            editorId: Math.floor(Math.random() * 1000000),
            localContent: this.value,
            activeFont: 'Arial',
            activeSize: '14',
            activeColor: '#000000',
            fontSearchQuery: '',
            sizeSearchQuery: '',
            fontList,
            fontSizeList,
        };
    },
    computed: {
        editorOptions() {
            return {
                theme: 'snow',
                modules: {
                    toolbar: `#quill-toolbar-${this.editorId}`,
                    imageResize: {
                        displayStyles: {
                            backgroundColor: 'black',
                            border: 'none',
                            color: 'white',
                        },
                        modules: ['Resize', 'DisplaySize'],
                    },
                },
                placeholder: 'Type something…',
            };
        },
    },
    watch: {
        value(val) {
            if (val !== this.localContent) {
                this.localContent = val;
            }
        },
        localContent(val) {
            this.$emit('input', val);
        },
    },
    methods: {
        onEditorReady(quill) {
            quill.on('selection-change', (range) => {
                if (range) {
                    const format = quill.getFormat(range);
                    this.activeFont = format.font || 'Arial';
                    this.activeSize = format.size ? parseInt(format.size).toString() : '14';
                    this.activeColor = format.color || '#000000';
                }
            });
            quill.on('text-change', () => {
                const range = quill.getSelection();
                if (range) {
                    const format = quill.getFormat(range);
                    this.activeFont = format.font || 'Arial';
                    this.activeSize = format.size ? parseInt(format.size).toString() : '14';
                    this.activeColor = format.color || '#000000';
                }
            });
        },
        applyCustomFont(val) {
            this.activeFont = val;
            if (this.$refs.myQuillEditor) {
                const quill = this.$refs.myQuillEditor.quill;
                quill.format('font', val);
            }
        },
        applyCustomSize(val) {
            this.activeSize = val;
            if (this.$refs.myQuillEditor && val) {
                const quill = this.$refs.myQuillEditor.quill;
                quill.format('size', String(val));
            }
        },
        applyCustomColor(val) {
            this.activeColor = val;
            if (this.$refs.myQuillEditor && val) {
                const quill = this.$refs.myQuillEditor.quill;
                quill.format('color', val);
            }
        },
        onContentChange({ html }) {
            this.localContent = html;
        },
    },
};
</script>

<template>
    <div class="text-editor-wrapper">

        <!-- Custom Toolbar Configuration -->
        <div :id="`quill-toolbar-${editorId}`" class="d-flex align-items-center p-2 bg-light border-bottom">

            <!-- Custom Vue Dropdowns for Font, Size & Color -->
            <span class="ql-formats d-flex align-items-center gap-5 mb-0 px-2" style="border-right: 1px solid #ccc;"
                @mousedown.stop>
                <div style="width: 150px;">
                    <SelectDropdown :modelValue="activeFont" :options="fontList" placeholder="Font"
                        @update:modelValue="applyCustomFont" :isFontFamily="true" :showSearch="true"
                        :searchQuery="fontSearchQuery" @update:searchQuery="fontSearchQuery = $event" />
                </div>
                <div style="width: 100px;">
                    <SelectDropdown :modelValue="activeSize" :options="fontSizeList" placeholder="Size"
                        @update:modelValue="applyCustomSize" :searchQuery="sizeSearchQuery"
                        @update:searchQuery="sizeSearchQuery = $event" />
                </div>
                <div style="width: 150px;">
                    <ColorPicker :modelValue="activeColor" @change="applyCustomColor" />
                </div>
            </span>

            <!-- Formatting Buttons -->
            <span class="ql-formats">
                <button class="ql-bold"></button>
                <button class="ql-italic"></button>
                <button class="ql-underline"></button>
            </span>
            <span class="ql-formats">
                <button class="ql-list" value="ordered"></button>
                <button class="ql-list" value="bullet"></button>
            </span>

            <span class="ql-formats">
                <button class="ql-image"></button>
            </span>
            <span class="ql-formats">
                <select class="ql-align"></select>
            </span>
            <span class="ql-formats">
                <button class="ql-clean"></button>
            </span>
        </div>

        <quill-editor ref="myQuillEditor" v-model="localContent" :options="editorOptions" @change="onContentChange"
            class="rte-quill" @ready="onEditorReady" />
    </div>
</template>

<style lang="scss">
.gap-5 {
    gap: 5px;
}

.ql-editor {
    min-height: 100px;
    max-height: 400px;
    overflow-y: scroll;
}

/* Quill color picker fix – make sure it renders above modal overlay */
.rte-modal .ql-snow .ql-tooltip {
    z-index: 9999 !important;
}

/* Custom Font Sizes for Quill: Generate classes up to 200px */
@for $i from 1 through 200 {
    .ql-size-#{$i} {
        font-size: #{$i}px !important;
    }
}

/* Ensure toolbar dropdown shows correct labels for standard sizes */
$dropdownSizes: 8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 44, 46, 48, 50, 52, 60, 72, 96;

@each $size in $dropdownSizes {

    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="#{$size}"]::before,
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="#{$size}"]::before {
        content: "#{$size}" !important;
    }
}

/* Custom Font Support for Quill */
.ql-font-Arial {
    font-family: Arial, sans-serif;
}

.ql-font-Calibri {
    font-family: Calibri, sans-serif;
}

.ql-font-Garamond {
    font-family: Garamond, serif;
}

.ql-font-Georgia {
    font-family: Georgia, serif;
}

.ql-font-Helvetica {
    font-family: Helvetica, sans-serif;
}

.ql-font-Inter {
    font-family: Inter, sans-serif;
}

.ql-font-Italic {
    font-family: Italic, serif;
}

.ql-font-Lato {
    font-family: Lato, sans-serif;
}

.ql-font-Manrope {
    font-family: Manrope, sans-serif;
}

.ql-font-Montserrat {
    font-family: Montserrat, sans-serif;
}

.ql-font-Open-Sans {
    font-family: 'Open Sans', sans-serif;
}

.ql-font-Oswald {
    font-family: Oswald, sans-serif;
}

.ql-font-Playfair-Display {
    font-family: 'Playfair Display', serif;
}

.ql-font-Poppins {
    font-family: Poppins, sans-serif;
}

.ql-font-Roboto {
    font-family: Roboto, sans-serif;
}

.ql-font-sans-serif {
    font-family: sans-serif;
}

.ql-font-Satoshi {
    font-family: Satoshi, sans-serif;
}

.ql-font-Times-New-Roman {
    font-family: 'Times New Roman', serif;
}

.ql-font-Urbanist {
    font-family: Urbanist, sans-serif;
}

.ql-font-Verdana {
    font-family: Verdana, sans-serif;
}

/* Also ensure toolbar dropdown shows correct labels/previews if possible */
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Arial"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Arial"]::before {
    content: "Arial";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Calibri"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Calibri"]::before {
    content: "Calibri";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Garamond"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Garamond"]::before {
    content: "Garamond";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Georgia"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Georgia"]::before {
    content: "Georgia";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Helvetica"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Helvetica"]::before {
    content: "Helvetica";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Inter"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Inter"]::before {
    content: "Inter";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Italic"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Italic"]::before {
    content: "Italic";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Lato"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Lato"]::before {
    content: "Lato";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Manrope"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Manrope"]::before {
    content: "Manrope";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Montserrat"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Montserrat"]::before {
    content: "Montserrat";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Open Sans"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Open Sans"]::before {
    content: "Open Sans";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Oswald"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Oswald"]::before {
    content: "Oswald";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Playfair Display"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Playfair Display"]::before {
    content: "Playfair Display";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Poppins"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Poppins"]::before {
    content: "Poppins";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Roboto"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Roboto"]::before {
    content: "Roboto";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="sans-serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="sans-serif"]::before {
    content: "Sans Serif";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Satoshi"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Satoshi"]::before {
    content: "Satoshi";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Times New Roman"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Times New Roman"]::before {
    content: "Times New Roman";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Urbanist"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Urbanist"]::before {
    content: "Urbanist";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Verdana"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Verdana"]::before {
    content: "Verdana";
}
</style>
